/**
 * Signed-in customers get their quiz result auto-saved to their account so it
 * survives a device change. Anonymous visitors keep using the lead-capture
 * path in `quiz-lead.functions.ts`.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const saveSchema = z.object({
  primaryGoal: z.string().max(40).nullable().optional(),
  symptoms: z.array(z.string().max(40)).max(20).default([]),
  duration: z.string().max(40).nullable().optional(),
  supplements: z.string().max(40).nullable().optional(),
  recommendedSku: z.string().max(80).nullable().optional(),
  supportingSku: z.string().max(80).nullable().optional(),
  bundle: z.boolean().default(false),
});

export interface MyQuizResult {
  id: string;
  primaryGoal: string | null;
  symptoms: string[];
  duration: string | null;
  supplements: string | null;
  recommendedSku: string | null;
  supportingSku: string | null;
  bundle: boolean;
  createdAt: string;
}

/** AUTHENTICATED. Store the latest quiz outcome on the caller's account. */
export const saveMyQuizResult = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => saveSchema.parse(d))
  .handler(async ({ data, context }): Promise<{ ok: boolean }> => {
    const { error } = await context.supabase.from("quiz_results").insert({
      user_id: context.userId,
      primary_goal: data.primaryGoal ?? null,
      symptoms: data.symptoms,
      duration: data.duration ?? null,
      supplements: data.supplements ?? null,
      answers: {
        primary: data.primaryGoal ?? null,
        symptoms: data.symptoms,
        duration: data.duration ?? null,
        supplements: data.supplements ?? null,
      },
      recommended_sku: data.recommendedSku ?? null,
      supporting_sku: data.supportingSku ?? null,
      bundle: data.bundle,
    });
    if (error) {
      console.error("[quiz-result] insert failed", error.message);
      return { ok: false };
    }
    return { ok: true };
  });

/** AUTHENTICATED. Latest saved quiz result for the account page. */
export const getMyQuizResult = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ result: MyQuizResult | null }> => {
    const { data } = await context.supabase
      .from("quiz_results")
      .select(
        "id, primary_goal, symptoms, duration, supplements, recommended_sku, supporting_sku, bundle, created_at",
      )
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!data) return { result: null };
    return {
      result: {
        id: data.id,
        primaryGoal: data.primary_goal,
        symptoms: data.symptoms ?? [],
        duration: data.duration,
        supplements: data.supplements,
        recommendedSku: data.recommended_sku,
        supportingSku: data.supporting_sku,
        bundle: data.bundle,
        createdAt: data.created_at,
      },
    };
  });
